/**
 * Screens the account and sign-in fixes of 2026-10-29 add around the app:
 * - the web (or desktop) app turned off in the console (`web_client`,
 *   `desktop_client`): a calm screen, signed in or out, that checks again;
 * - the old address's "This wasn't me" link (`?undo_email=`): the email goes
 *   back, every device is signed out, and the new-password screen opens.
 * A server invite link (`/invite/CODE`) is shell/InviteLink.tsx (one reader
 * for the link, signed in or out).
 */
import { useEffect, useState } from 'react';
import markUrl from '../assets/mark.svg';
import { errorText } from '../auth/Auth';
import { forgetLinkParam, offerLinkParam, takeLinkParam } from '../auth/links';
import { tr, useT } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { desktopBridge } from '../lib/config';
import { flagOn, loadConfig, useConfig } from '../store/config';
import { signOut, useSession } from '../store/session';
import { Btn, Icon } from '../ui/kit';
import { toast } from '../ui/overlay';

/** Which app is turned off for this window: 'web', 'desktop', or null when it may run. */
export function useAppOff(): 'web' | 'desktop' | null {
  const config = useConfig((s) => s.config);
  const kind = desktopBridge() ? 'desktop' : 'web';
  return flagOn(kind === 'web' ? 'web_client' : 'desktop_client', config) ? null : kind;
}

export function AppOffScreen({ kind }: { kind: 'web' | 'desktop' }) {
  const { t, fmt } = useT();
  const signedIn = useSession((s) => s.status !== 'signed_out' && s.status !== 'checking');
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(() => Date.now());
  const check = async () => {
    setChecking(true);
    await loadConfig({ force: true });
    setChecked(Date.now());
    setChecking(false);
  };
  useEffect(() => {
    const id = setInterval(() => void check(), 60_000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="ns-splash ns-maint ns-app-off" role="main">
      <div className="nx-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>
      <div className="ns-maint-card nx-raised">
        <img src={markUrl} alt="" width={48} height={48} className="ns-splash-mark" />
        <Icon name="power" />
        <h1>{t(`app.off.${kind}.title`)}</h1>
        <p>{t(`app.off.${kind}.body`)}</p>
        <Btn kind="ghost" size="sm" busy={checking} onClick={() => void check()}>
          {t('app.off.retry')}
        </Btn>
        <span className="ns-muted">{t('cfg.mt.checked', { time: fmt.time(checked) })}</span>
        {signedIn ? (
          <Btn kind="quiet" size="sm" icon="power" onClick={() => void signOut()}>
            {t('app.off.signOut')}
          </Btn>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The "This wasn't me" link: true while it is being used (the app shows a
 * splash), then the new-password screen opens on the sign-in screen.
 */
export function useUndoEmailLink(): boolean {
  const [token] = useState(() => takeLinkParam('undo_email'));
  const [working, setWorking] = useState(!!token);
  useEffect(() => {
    if (!token) return;
    // once per link (StrictMode runs effects twice)
    forgetLinkParam('undo_email');
    const { t } = tr();
    api.auth
      .undoEmailChange({ token })
      .then((r) => {
        // The new-password screen opens with it: on the sign-in screen, or (signed
        // in as someone else here) in the reset dialog. That account's own sessions
        // were all ended by the server, so a session of it here signs out by itself.
        offerLinkParam('reset_password', r.reset_token);
        toast(t('email.undo.done'), { icon: 'shield-check', tone: 'ok', duration: 9000 });
      })
      .catch((e: unknown) => {
        toast(isApiRequestError(e) && e.code !== 0 ? errorText(e, t) : t('ns.err.generic'), {
          icon: 'alert',
          tone: 'danger',
          duration: 9000,
        });
      })
      .finally(() => setWorking(false));
  }, [token]);
  return working;
}
